/* eslint-disable react-hooks/exhaustive-deps */
import { ApolloClient, ApolloProvider, createHttpLink, from, InMemoryCache } from "@apollo/client"
import { Box, ChakraProvider, Flex } from "@chakra-ui/react"
import React, { useEffect } from "react"
import { HelmetProvider } from "react-helmet-async"
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom"
import { chakraTheme } from "./theme/chakraTheme"
/* Theme variables */
import { setContext } from "@apollo/client/link/context"
import { onError } from "@apollo/client/link/error"
import { useAtom } from "jotai"
import { FORBIDDEN_ACCESS_MESSAGE, headerFooterContentWidth, headerFooterMaxWidth, Paths, paths } from "./common/commonConstants"
import { useScrollToTop } from "./common/commonHooks"
import { AuthErrorHandler, triggerAuthError } from "./components/auth/AuthErrorHandler"
import { TokenValidator } from "./components/auth/TokenValidator"
import { Footer } from "./components/Footer/footer.main"
import { Header } from "./components/Header"
import { userDetailsAtom } from "./components/Menu/menu.store"
import { getSavedUserDetails, getToken, isProtectedRoute } from "./components/Menu/menu.support"
import { Accrediation } from "./pages/accrediation"
import { AllAccessories } from "./pages/all-accessories"
import { AllSealants } from "./pages/all-sealants"
import { CompliancePage } from "./pages/Compliance"
import { Contact } from "./pages/contactUs/contactus"
import { ContactUsConfirmation } from "./pages/contactUs/contactus.confirmation"
import { Distributors } from "./pages/distributors"
import { DrawingAndRevit } from "./pages/drawing-and-revit"
import Error from "./pages/Error"
import { FAQ } from "./pages/faq/faq"
import { EditFireManagementPlan } from "./pages/fmp/editFireManagementPlan/EdittFireManagementPlan"
import { MyArchivedFMPs } from "./pages/fmp/myFMPs/myArchivedFMPs"
import { MyFMPs } from "./pages/fmp/myFMPs/myFMPs"
import { ProductFilter } from "./pages/fmp/productFilter/productFilter.main"
import { PublicProductFilter } from "./pages/productFilter/productFilter.public"
import { ViewFireManagementPlan } from "./pages/fmp/viewFireManagementPlan/ViewFireManagementPlan"
import { HomePage } from "./pages/homePage/homePage"
import { InstallationInstructions } from "./pages/Installation-instructions"
import { ChangePassword } from "./pages/login/changePassword"
import { Login } from "./pages/login/login"
import { Logout } from "./pages/login/logout"
import { ResetPassword } from "./pages/login/resetPassword"
import { SignUpConfirmation } from "./pages/login/signupNew/signup.confirmation"
import { VerificationSuccess } from "./pages/login/verificationSuccess"
import { Product } from "./pages/product"
import { AllProducts } from "./pages/products/all-products"
import { CastInFireCollars } from "./pages/products/castInFireCollars"
import { FloorwasteAndShower } from "./pages/products/floorwasteAndShower"
import { StackFireCollars } from "./pages/products/stackFireCollars"
import { EndlessCollars } from "./pages/products/endlessCollars"
import { GasFireCollars } from "./pages/products/gasFireCollars"
import { MultiServiceFireCollars } from "./pages/products/MultiServiceFireCollars"
import { RetroFitFireCollars } from "./pages/products/RetroFitFireCollars"
import { ProductSubPage } from "./pages/productsubPage"
import { Report } from "./pages/report"
import { ReportsAndResources } from "./pages/reports/reportsAndResources"
import { SealantSubPage } from "./pages/sealant-subpage"
import { TermsAndConditions } from "./pages/termsAndConditions"
import { TestReports } from "./pages/test-reports"
import { UniqueSolutions } from "./pages/unique-solutions"
import "./theme/variables.css"

const httpLink = createHttpLink({
  uri: paths.REMOTE
})

const authLink = setContext((_, { headers }) => {
  // get the authentication token from storage (checks both localStorage and sessionStorage)
  const token = getToken()
  const authorizationHeader = token ? `Bearer ${token}` : ""

  return {
    headers: {
      ...headers,
      authorization: authorizationHeader
    }
  }
})

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions }) => {
      // Only redirect on authentication/authorization errors, not on query errors
      // Check if it's an actual 403/401 error or authentication-related
      const response = extensions?.response as { statusCode?: number } | undefined
      const isAuthError =
        message === FORBIDDEN_ACCESS_MESSAGE ||
        extensions?.code === "FORBIDDEN" ||
        extensions?.code === "UNAUTHENTICATED" ||
        (response?.statusCode && [401, 403].includes(response.statusCode))

      if (isAuthError) {
        // Only redirect on protected routes and not already on login or logout pages
        const currentPath = window.location.pathname
        if (currentPath !== Paths.LOGIN.login && currentPath !== Paths.LOGIN.logout && isProtectedRoute(currentPath)) {
          // Trigger auth error handling via custom event
          // This allows React Router navigation without page reload
          triggerAuthError(currentPath)
        }
      }
    })
  }
  if (networkError) {
    // Network error handling - don't redirect on network errors
    console.error("Network error:", networkError)
  }

  // Return forward(operation) to allow the error to propagate to component error handlers
  return forward(operation)
})

const client = new ApolloClient({
  link: from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network"
    }
  }
})

interface MainPageProps {
  children: React.ReactNode
}
export const MainPage = ({ children }: MainPageProps): JSX.Element => (
  <Box w={headerFooterContentWidth} maxW={headerFooterMaxWidth} margin={"auto"}>
    {children}
  </Box>
)

// Component that handles routing and scroll-to-top functionality
const AppRoutes = (): JSX.Element => {
  // Scroll to top when route changes - now inside router context
  useScrollToTop()

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/homepage" />} />
      <Route path="/homepage" element={<HomePage />} />
      <Route path={Paths.Product.all} element={<AllProducts />} />
      <Route path={Paths.Product.sub.castIn.all} element={<Navigate to={Paths.Product.sub.floorwasteAndShower.all} replace />} />
      <Route path={Paths.Product.sub.castIn.product} element={<Product />} />
      <Route path={Paths.Product.sub.floorwasteAndShower.all} element={<FloorwasteAndShower />} />
      <Route path={Paths.Product.sub.stack.all} element={<StackFireCollars />} />
      <Route path={Paths.Product.sub.retroFit.all} element={<RetroFitFireCollars />} />
      <Route path={Paths.Product.sub.retroFit.product} element={<Product />} />
      <Route path={Paths.Product.sub.endless.all} element={<EndlessCollars />} />
      <Route path={Paths.Product.sub.endless.product} element={<Product />} />
      <Route path={Paths.Product.sub.gas.all} element={<GasFireCollars />} />
      <Route path={Paths.Product.sub.gas.product} element={<Product />} />
      <Route path={Paths.Product.sub.accessory.all} element={<AllAccessories />} />
      <Route path={Paths.Product.sub.accessory.product} element={<ProductSubPage />} />
      <Route path={Paths.Product.sub.accessories.all} element={<AllAccessories />} />
      <Route path={Paths.Product.sub.accessories.product} element={<ProductSubPage />} />
      <Route path={Paths.Product.sub.sealant.all} element={<AllSealants />} />
      <Route path={Paths.Product.sub.sealant.category} element={<SealantSubPage />} />
      <Route path={Paths.Product.sub.multiservice.all} element={<MultiServiceFireCollars />} />
      <Route path={Paths.Product.sub.multiservice.product} element={<Product />} />
      <Route path={Paths.Product.sub.fireCollar.product} element={<Product />} />
      <Route path={Paths.Product.sub.fireCollar.all} element={<AllProducts />} />
      <Route path={Paths.Product.sub.accessory.product} element={<Product />} />
      <Route path="/fire-collar/:id" element={<Product />} />

      {/* static pages */}
      <Route path={Paths.DISTRIBUTORS} element={<Distributors />} />
      <Route path="/accessories-sub" element={<ProductSubPage />} />
      <Route path="/reports/all" element={<ReportsAndResources />} />
      <Route path="/report/:reportNo" element={<Report />} />
      <Route path="/installationInstruction/:reportNo" element={<Report />} />
      <Route path="/testReport/:reportNo" element={<Report />} />
      <Route path="/qualityCertificate/:reportNo" element={<Report />} />
      <Route path={Paths.TEST_REPORTS} element={<TestReports />} />
      <Route path={Paths.UNIQUE_SOLUTIONS} element={<UniqueSolutions />} />
      <Route path={Paths.INSTALLATION_INSTRUCTIONS} element={<InstallationInstructions />} />
      <Route path={Paths.DRAWINGS} element={<DrawingAndRevit />} />
      <Route path={Paths.FAQ} element={<FAQ />} />
      <Route path={Paths.COMPLIANCE} element={<CompliancePage />} />
      <Route path={Paths.QUALITY} element={<Accrediation />} />
      <Route path={Paths.CONTACTUS} element={<Contact />} />
      <Route path="/contactUsConfirmation" element={<ContactUsConfirmation />} />

      {/* Public product filter */}
      <Route path={Paths.PRODUCT_FILTER} element={<PublicProductFilter />} />

      {/* FMP */}
      <Route path={Paths.FMP.PRODUCT_FILTER} element={<ProductFilter />} />
      <Route path={Paths.FMP.MY_FMP} element={<MyFMPs />} />
      <Route path={Paths.FMP.MY_ARCHIVED_FMP} element={<MyArchivedFMPs />} />
      <Route path={Paths.FMP.EDIT_FMP} element={<EditFireManagementPlan />} />
      <Route path={Paths.FMP.VIEW_FMP} element={<ViewFireManagementPlan />} />
      <Route path={Paths.ERROR} element={<Error />} />
      <Route path="/verificationSuccess" element={<VerificationSuccess />} />
      <Route path="/resetPassword" element={<ResetPassword onBack={() => {}} />} />
      <Route path="/changePassword" element={<ChangePassword />} />
      <Route path={Paths.TERMSANDCONDITIONS} element={<TermsAndConditions />} />
      <Route path={Paths.PRIVACYPOLICY} element={<TermsAndConditions />} />
      <Route path={Paths.LOGIN.login} element={<Login />} />
      <Route path={Paths.LOGIN.logout} element={<Logout />} />
      <Route path={"/test"} element={<SignUpConfirmation />} />

      <Route
        path={Paths.SIGNUP_CONFIRMATION}
        element={
          <Box width={["100%"]} margin="auto" minHeight={"70vh"}>
            <SignUpConfirmation />
          </Box>
        }
      />

      {/* Fallback Route */}
      <Route path="*" element={<Navigate to="/homepage" />} />
    </Routes>
  )
}

// Layout component that conditionally renders header and footer
const AppLayout = (): JSX.Element => {
  const location = useLocation()
  const isReportRoute = location.pathname.startsWith("/report/")
  const isChangePasswordRoute = location.pathname.startsWith("/changePassword")

  return (
    <Box as="div" role="application" aria-label="Snap Fire Systems Website" minHeight="100vh" display="flex" flexDirection="column">
      <AuthErrorHandler />
      <TokenValidator />
      {!isReportRoute && !isChangePasswordRoute && (
        <Flex
          bg={"primary"}
          w="100%"
          position={"sticky"}
          top="0"
          zIndex={"100"}
          align={"center"}
          role="header"
          aria-label="Site header"
          id="main-navigation"
        >
          <MainPage>
            <Header />
          </MainPage>
        </Flex>
      )}
      <Box flex="1" minHeight={["320px", "320px", "320px", "445px", "445px", "445px"]}>
        <AppRoutes />
      </Box>
      {!isReportRoute && !isChangePasswordRoute && <Footer />}
    </Box>
  )
}

const App: React.FC = () => {
  const savedUserDetails = getSavedUserDetails()
  const [, setUserDetails] = useAtom(userDetailsAtom)

  useEffect(() => {
    if (savedUserDetails) {
      setUserDetails(savedUserDetails)
    }
  }, [])

  return (
    <React.StrictMode>
      <ChakraProvider theme={chakraTheme}>
        <ApolloProvider client={client}>
          <HelmetProvider>
            <BrowserRouter>
              <AppLayout />
            </BrowserRouter>
          </HelmetProvider>
        </ApolloProvider>
      </ChakraProvider>
    </React.StrictMode>
  )
}

export default App
